import type { OrgAction } from '@1qa/shared';
import { Clapperboard, Gauge, MonitorSmartphone, ShieldCheck, type IconComponent } from '@/components/icons';

/**
 * Helper services: tools that run beside 1QA to help with a kind of testing (the browser recorder for web tests, the
 * Device Agent for real phones). They live on one page, /helpers, each on its own sub-page. A new one (e.g. a
 * security scanner or a load generator) is one entry here plus its route in App.tsx; `available: false` lists it
 * as coming later.
 */
export interface HelperService {
  id: string;
  /** Sub-page path under /helpers. */
  path: string;
  label: string;
  /** What kind of testing it helps with. */
  area: string;
  description: string;
  icon: IconComponent;
  /** Organization permission needed to use it. */
  permission: OrgAction;
  available: boolean;
}

export const HELPER_SERVICES: HelperService[] = [
  {
    id: 'recorder',
    path: 'recorder',
    label: 'Browser recorder',
    area: 'Web',
    description: 'A Chrome / Edge extension: record what you do in the browser and save it as a 1QA test.',
    icon: Clapperboard,
    permission: 'test.write',
    available: true,
  },
  {
    id: 'devices',
    path: 'devices',
    label: 'Devices',
    area: 'Mobile',
    description: 'The 1QA Device Agent: run Android tests on real phones plugged into a tester’s computer.',
    icon: MonitorSmartphone,
    permission: 'run.trigger',
    available: true,
  },
  {
    id: 'security',
    path: 'security',
    label: 'Security scanner',
    area: 'Security',
    description: 'Scan your app for common security problems while your tests run.',
    icon: ShieldCheck,
    permission: 'run.trigger',
    available: false,
  },
  {
    id: 'performance',
    path: 'load-agents',
    label: 'Load agents',
    area: 'Performance',
    description: 'The 1QA Load Agent: machines that put your app under load in performance tests.',
    icon: Gauge,
    permission: 'perf.run',
    available: true,
  },
];

/** The helper services the user may open (available ones). */
export const usableHelperServices = (can: (action: OrgAction) => boolean) => HELPER_SERVICES.filter((s) => s.available && can(s.permission));
